import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Typography, Tag, Space, Button, Input, Form, Row, Tooltip, Avatar } from 'antd';
import { SearchOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, FileTextOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { tenantActions } from '../../actions/tenantActions';
import Paginations from '../../components/pagination';
import { getblobLink } from '../../services/helper';
import TableData from '../../components/table';
import FilterPopover from '../../components/filter';
import dayjs from 'dayjs';

const { Text } = Typography;

interface TenantItem {
  _id: string;
  name: string;
  email?: string;
  phoneNumber?: string;
  country?: string;
  address?: string;
  isActive?: boolean;
  createdAt?: string;
  validityStartDate?: string;
  validityEndDate?: string;
  branchCount?: number;
  children?: TenantItem[];
}

const TenantList: React.FC = () => {
  const hasFetchedInitialData = useRef(false);
  const mountCount = useRef(0);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isFilterData, setIsFilter] = useState(false);
  const [filterData, setIsFilterData] = useState({ status: "", validityStatus: "" });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [filterForm] = Form.useForm();

  // Admin redirect: if Admin lands on tenant list, redirect to own tenant view
  useEffect(() => {
    try {
      const auth = JSON.parse(localStorage.getItem("AUTHDATA") || '{}');
      if (auth?.role === 'ADMIN' && auth?.tenantId) {
        navigate(`/tenants/${auth.tenantId}`, { replace: true });
      }
    } catch (_) { /* ignore */ }
  }, [navigate]);

  const defaultPayLoad = { page: 1, limit: 10 };
  const [pageSize, setPageSize] = useState<number>(Number(defaultPayLoad.limit));
  const [appliedFilters, setAppliedFilters] = useState<Record<string, any>>({});

  const tenantListState = useSelector((state: any) => state.getTenantListReducer);
  const isListing = Boolean(tenantListState?.isLoading);
  const tenants = tenantListState?.tenantData?.tenants ?? [];
  const totalCount = tenantListState?.tenantData?.pagination?.totalRecords ?? 0;

  useEffect(() => {
    mountCount.current += 1;
    if (mountCount.current === 2 && currentPage === 1 && !debouncedSearchText?.trim()) {
      return;
    }

    const payload: any = {
      pageNumber: currentPage,
      pageSize,
    };

    if (debouncedSearchText) {
      payload.searchText = debouncedSearchText;
    }

    if (isFilterData && filterData && typeof filterData === 'object') {
      Object.entries(filterData).forEach(([k, v]) => {
        if (v !== undefined && v !== null && String(v).trim() !== '') {
          payload[k] = v;
        }
      });
    }

    dispatch(tenantActions.getTenantList(payload));
  }, [currentPage, pageSize, debouncedSearchText, isFilterData, filterData, dispatch]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    if (debouncedSearchText) {
      setCurrentPage(1);
    }
  }, [debouncedSearchText]);

  const handleViewTenant = (tenant: any) => {
    navigate(`/tenants/view`, { state: { tenantId: tenant._id } });
  };

  const getValidityStatus = (tenant: TenantItem) => {
    if (!tenant.validityEndDate) return { status: 'active', text: 'Active', color: 'success' };

    const endDate = dayjs(tenant.validityEndDate);
    const now = dayjs();

    if (endDate.isBefore(now)) {
      return { status: 'expired', text: 'Expired', color: 'error' };
    }

    const daysRemaining = endDate.diff(now, 'day');
    if (daysRemaining <= 7) {
      return { status: 'expiring', text: `Expires in ${daysRemaining}d`, color: 'warning' };
    }

    return { status: 'active', text: 'Active', color: 'success' };
  };

  const showModal = (tenant?: any) => {
    if (tenant) {
      navigate(`/tenants/view`, { state: { tenantId: tenant._id } });
    }
  };

  const getTenantColumns = (isChild = false) => {
    const columns: any[] = [
      {
        title: "Tenant",
        dataIndex: "name",
        key: "name",
        render: (_: string, record: TenantItem) => (
          <Space align="center">
            <Avatar size={36} src={`${getblobLink()}/public/asset/tenant.png`} />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                marginLeft: 8,
              }}
            >
              <Text strong>{record.name || "-"}</Text>
              {record.email && (
                <Text type="secondary" className="text-small">
                  {record.email}
                </Text>
              )}
            </div>
          </Space>
        ),
      },

      {
        title: "Contact",
        dataIndex: "phoneNumber",
        key: "phoneNumber",
        render: (_: string, record: TenantItem) => (
          <Space direction="vertical" size={0}>
            <Text>{record.phoneNumber || "-"}</Text>
            {record.country && (
              <Text type="secondary" className="text-small">
                {record.country}
              </Text>
            )}
          </Space>
        ),
      },

      {
        title: "Validity",
        key: "validityStatus",
        render: (_: any, record: TenantItem) => {
          const validityStatus = getValidityStatus(record);

          return (
            <Tag color={validityStatus.color} style={{ border: "1px solid" }}>
              {validityStatus.text}
            </Tag>
          );
        },
      },

      {
        title: "Status",
        dataIndex: "isActive",
        key: "isActive",
        render: (isActive: boolean) => (
          <Tag
            icon={
              isActive
                ? <CheckCircleOutlined />
                : <CloseCircleOutlined />
            }
            color={isActive ? "green" : "default"}
            style={{ border: "1px solid" }}
          >
            {isActive ? "Active" : "Inactive"}
          </Tag>
        ),
      },

      {
        title: "Created At",
        dataIndex: "createdAt",
        key: "createdAt",
        render: (text: string) =>
          text
            ? new Date(text).toLocaleString()
            : "-",
      },

      {
        title: "Actions",
        key: "actions",
        render: (_: any, record: TenantItem) => (
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleViewTenant(record);
              }}
            />
          </Tooltip>
        ),
      },
    ];

    if (!isChild) {
      console.log("Adding Branches column for main tenants");
      columns.splice(6, 0, {
        title: "Branches",
        dataIndex: "branchCount",
        key: "branchCount",
        render: (count: number) => (
          <Tag color="blue" style={{ border: "1px solid" }}>
            {count || 0}
          </Tag>
        ),
      });
    }

    return columns;
  }

  const columns = getTenantColumns(false);


  const handleOnPageChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page);
    if (newPageSize) setPageSize(newPageSize);
  };

  const handleApplyFilter = (values: any) => {
    setIsFilter(true);
    setCurrentPage(1);
    const newFilterData: any = { status: "", validityStatus: "" };
    const appliedFiltersTemp: any = {};

    if (values?.status !== undefined && values?.status !== null && values?.status !== '') {
      newFilterData.status = values.status;
      appliedFiltersTemp.status = values.status;
    }

    if (values?.validityStatus !== undefined && values?.validityStatus !== null && values?.validityStatus !== '') {
      newFilterData.validityStatus = values.validityStatus;
      appliedFiltersTemp.validityStatus = values.validityStatus;
    }

    setIsFilterData(newFilterData);
    setAppliedFilters(appliedFiltersTemp);
  };

  const handleClearFilter = () => {
    setCurrentPage(1);
    setIsFilter(false);
    setIsFilterData({ status: "", validityStatus: "" });
    setAppliedFilters({});
  };

  const filterFields = useMemo(() => [
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      placeholder: 'Select Status',
      options: [
        { label: 'Active', value: 'true' },
        { label: 'Inactive', value: 'false' },
      ],
    },
    {
      name: 'validityStatus',
      label: 'Validity Status',
      type: 'select',
      placeholder: 'Select Validity Status',
      options: [
        { label: 'Active', value: 'active' },
        { label: 'Expiring Soon', value: 'expiring' },
        { label: 'Expired', value: 'expired' },
      ],
    },
  ], []);

  return (
    <div className="tenants-page">
      <Row justify="space-between" align="middle" className="mb-4">
          <div className="category-toolbar">
            <Input
              className="tenants-search-input"
              placeholder="Search by name, email, or phone"
              prefix={<SearchOutlined />}
              allowClear
              onChange={e => setSearchText(e.target.value)}
            />
            <FilterPopover
              form={filterForm}
              fields={filterFields}
              initialValues={appliedFilters}
              appliedCount={Object.keys(appliedFilters).length}
              onApply={handleApplyFilter}
              onClear={handleClearFilter}
            />
            <Button
              icon={<FileTextOutlined />}
              type="default"
              onClick={() => navigate('/tenants/purchaseHistory')}
            >
              Purchases
            </Button>
          </div>
      </Row>

      <div className="driveMynt-card">
        <TableData
          className="tenant-table"
          rowKey="_id"
          loading={isListing}
          columns={columns}
          dataSource={tenants}
          pagination={false}
          expandable={{
            expandedRowRender: (record: TenantItem) => (
              <div className="tenant-branch-panel">
                <div className="tenant-branch-header">
                  <div>
                    <Text strong>
                      Branches ({record.children?.length || 0})
                    </Text>
                  </div>
                </div>

                <TableData
                  rowKey="_id"
                  columns={getTenantColumns(true)}
                  dataSource={record.children || []}
                  pagination={false}
                  size="small"
                  className="tenant-branch-table"
                  onRow={(branch: any) => ({
                    onDoubleClick: () =>
                      showModal(branch),
                    style: {
                      cursor: "pointer",
                    },
                  })}
                />
              </div>
            ),

            rowExpandable: (record: TenantItem) =>
              (record.children?.length || 0) > 0,

            expandRowByClick: false,
          }}
          onRow={(record: any) => ({
            onDoubleClick: () =>
              showModal(record),

            style: {
              cursor: "pointer",
            },
          })}
        />
        <Paginations
          total={totalCount}
          defaultCurrent={currentPage}
          defaultPageSize={pageSize}
          callback={handleOnPageChange}
        />
      </div>
    </div>
  );
};

export default TenantList;
